<template>
    <SidePanelHeader></SidePanelHeader>
    <div
        class="loader-block-select"
        v-if="loading || $route.params.percentComplete != '100'"
        role="alert" title="Data is loading"
    >
        <div class="spinner" id="loading-bar-spinner">
            <div class="spinner-icon"></div>
        </div>
    </div>
    <div class="header-btn-block" v-on:click="onBack" v-if="!loading && entities.length > 0" role="navigation">
        <a class="back-btn">
            <img src="@/assets/arrow-left.svg" alt="Back Button" />
            {{ $t("EntitySelectView.back") }}
        </a>
    </div>
    <div role="main">
        <div v-show="!showResults">
            <div v-show="$route.params.percentComplete != '100'">
                <div class="display-row">
                    <div class="progress-bar-background">
                        <div
                            role="progressbar"
                            :aria-valuenow="Number($route.params.percentComplete)"
                            class="progress-bar-foreground"
                            :style="'width:' + $route.params.percentComplete + '%'"
                        >
                            {{ $route.params.percentComplete }}%
                        </div>
                    </div>
                </div>
                <div class="display-row">
                    <p class="num-highlight-text">
                        {{ $route.params.numHighlights }}
                        {{ $t("EntitySelectView.results_found") }}
                    </p>
                    <button class="cancel-button" v-on:click="stopScan">
                        {{ $t("EntitySelectView.stop_scan") }}
                    </button>
                </div>
            </div>
            <div v-show="$route.params.percentComplete == '100'">
                <div class="settings-block">
                    <div class="active-setting" v-if="modifiedSettingsCount">
                        <img
                            class="setting-cog"
                            src="@/assets/settings-cog.svg"
                            alt="Setting icon"
                        />
                        <div class="tooltip-main-data setting-data">
                            <h2
                                class="active-setting-text"
                                tabindex="0"
                                :aria-describedby="'active-settings'"
                            >
                                {{ modifiedSettingsCount }}
                                {{ $t("EntitySelectView.modified_settings") }}
                            </h2>
                            <div class="tooltip-data-filter" tabindex="0">
                                <p
                                    class="t-texts"
                                    title="Display all active settings"
                                >
                                    <span id="active-settings" role="tooltip">
                                        {{ pluginSettings?.descriptor?.join(", ") }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="active-setting">
                        <img
                            class="setting-cog"
                            src="@/assets/filter.svg"
                            alt="Filter icon"
                        />
                        <div class="tooltip-main-data">
                            <h2
                                class="active-setting-text"
                                tabindex="0"
                                :aria-describedby="'active-filters'"
                            >
                                {{ $t("EntitySelectView.entity_types") }}:
                                {{
                                    filterSettings.type?.filters.length < 11
                                        ? filterSettings.type?.filters.length
                                        : "All"
                                }}
                            </h2>
                            <div class="tooltip-data-filter" tabindex="0">
                                <p
                                    class="t-texts"
                                    title="Display Active Entity Types"
                                >
                                    <span id="active-filters">
                                        {{ 
                                            filterSettings.type?.filters.join(", ")
                                            || "All entities filtered"
                                        }}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <div class="tooltip-main-data dataset-data">
                            <h2
                                class="active-setting-text"
                                tabindex="0"
                                :aria-describedby="'active-datasets'"
                            >
                                {{ $t("EntitySelectView.datasets") }}:
                                {{
                                    filterSettings.graph?.filters.length < 8
                                        ? filterSettings.graph?.filters.length
                                        : "All"
                                }}
                            </h2>
                            <div class="tooltip-data-filter" tabindex="0">
                                <p class="t-texts" title="Display Active Dataset">
                                    <span id="active-datasets">
                                        {{
                                            filterSettings.graph?.filters.join(
                                                ", ",
                                            ) || "No datasets selected"
                                        }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="display-row-offcenter" v-if="Number($route.params.numHighlights) > 0">
                    <p class="num-results-info">
                        {{ $route.params.numHighlights }}
                        {{ $t("EntitySelectView.results_found") }}
                    </p>
                </div>
                <div class="display-row-offcenter" v-if="Number($route.params.numHighlights) > 0">
                    <h2 class="title">
                        <i class="arrow left"></i
                        >{{ $t("EntitySelectView.hover_on_a") }}
                    </h2>
                </div>
                <div class="entity-search-bar-container" v-if="Number($route.params.numHighlights) > 0">
                    <input 
                        class="entity-search-bar" 
                        :class="{
                            'entity-search-bar-failure': entitySearchNoResults 
                        }"
                        type="text" 
                        placeholder="Or search for an entity..." 
                        v-model="entitySearchValue"
                        v-on:keydown.enter="exactMatchScan"
                    >
                    <p v-if="entitySearchNoResults" class="entity-failure-message">No results found!</p>
                </div>
                <div class="display-row-offcenter" v-if="Number($route.params.numHighlights) > 0">
                    <svg
                        class="display-row"
                        style="
                            padding-left: 5px;
                            padding-right: 5px;
                            cursor: pointer;
                            height: 40px;
                        "
                        tabindex="0"
                        v-on:click="focusOnPreviousHighlight"
                        v-on:keydown.enter="focusOnPreviousHighlight"
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="10"
                        viewBox="0 0 14 8"
                        fill="none"
                    >
                        <path
                            d="M7.00023 2.828L2.05023 7.778L0.63623 6.364L7.00023 0L13.3642 6.364L11.9502 7.778L7.00023 2.828Z"
                            fill="#525455"
                        />
                    </svg>
                    <svg
                        class="display-row"
                        style="
                            padding-left: 5px;
                            padding-right: 5px;
                            cursor: pointer;
                            height: 40px;
                        "
                        tabindex="0"
                        v-on:click="focusOnNextHighlight"
                        v-on:keydown.enter="focusOnNextHighlight"
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="10"
                        viewBox="0 0 14 8"
                        fill="none"
                    >
                        <path
                            d="M7.00023 5.17205L11.9502 0.222046L13.3642 1.63605L7.00023 8.00005L0.63623 1.63605L2.05023 0.222046L7.00023 5.17205Z"
                            fill="#525455"
                        />
                    </svg>
                    <p
                        v-show="focusedHighlight > 0"
                        style="
                            padding-left: 5px;
                            user-select: none;
                            margin-top: 11px;
                        "
                    >
                        ({{ focusedHighlight }}/{{ $route.params.numHighlights }})
                    </p>
                </div>
                <NoResults v-if="Number($route.params.numHighlights) == 0"></NoResults>
            </div>
            <hr/>

            <!-- Show matched highlights -->
            <div
                class="matched-entity-section"
                role="list"
                aria-label="Scanned highlights"
                v-show="$route.params.percentComplete == '100' && !loading"
            >
                <div
                    style="display: block"
                    role="listitem"
                    :aria-label="highlight"
                    tabindex="0"
                    v-for="(highlight, index) in highlights"
                    :id="'highlight-' + index"
                    :key="index"
                    v-on:click="focusOnHighlight(index)"
                    v-on:keydown.enter="focusOnHighlight(index)"
                    :class="
                        focusedHighlight - 1 == index
                            ? 'matched-entities hovered'
                            : 'matched-entities'
                    "
                >
                    {{ highlight }}
                </div>
            </div>
        </div>

        <div class="matching-entities" v-show="entities.length > 0">
            <div>
                <h2 class="select-matching-entities">{{ $t("EntitySelectView.select_matching_entity") }}</h2>
                <h3 class="match-entities">
                    {{ entities.length }}
                    {{ $t("EntitySelectView.entities_matching") }}:
                </h3>
                <h4 class="match-title">"{{ $route.params.text }}"</h4>
            </div>
            <select
                v-model="typeFilter"
                v-on:change="filterByType"
                class="type-filter"
                title="Filter Entities By Type"
            >
                <option value="all">{{ $t("EntitySelectView.all_types") }}</option>
                <option
                    v-for="(type, index) in allTypes"
                    :key="index"
                    :value="type"
                >
                    {{ type }}
                </option>
            </select>
            <div class="entity-wrapper" role="list">
                <div 
                    class="tooltip-main-data entity-data"
                    v-for="(entity, index) in displayedEntities"
                    :key="index"
                    role="listitem"
                    v-on:mouseenter="tooltipActive(entity, $event, index)"
                >
                    <div
                        class="entity"
                        tabindex="0"
                        v-on:click="selectEntity(entity['label'], entity['uri'])"
                        v-on:keydown.enter="selectEntity(entity['label'], entity['uri'])"
                    >
                        <h5 class="entity-label" :title="entity['label']">
                            {{ entity["label"] }}
                        </h5>
                        <div class="entity-info-block">
                            <span class="entity-type" :aria-label="entity['type']">
                                {{ entity["type"] }}
                            </span>
                            <span v-if="entity['links'] != null">
                                &#183;
                            </span>
                            <div
                                class="entity-links-wrapper"
                                v-if="entity['links'] != null"
                            >
                                <span
                                    class="entity-links"
                                    :aria-label="entity['links']"
                                    >{{ entity["links"] }}</span
                                >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="12"
                                    height="7"
                                    viewBox="0 0 9 5"
                                    fill="none"
                                >
                                    <title>
                                        {{ $t("EntitySelectView.connected_entities") }}
                                    </title>
                                    <path
                                        d="M5.81259 1.72918L5.81259 2.35634C5.81261 2.64445 5.74472 2.92975 5.6128 3.19594C5.48088 3.46212 5.28751 3.70399 5.04374 3.90772C4.79997 4.11145 4.51057 4.27305 4.19207 4.3833C3.87357 4.49355 3.5322 4.55029 3.18745 4.55027L2.99998 4.55005C2.30375 4.55005 1.63604 4.31891 1.14373 3.90746C0.651423 3.49602 0.374848 2.93798 0.374848 2.35612C0.374848 1.77425 0.651424 1.21621 1.14373 0.80477C1.63604 0.393327 2.30375 0.162182 2.99998 0.162182L2.99998 0.789336C2.75307 0.788433 2.50837 0.828297 2.27993 0.906642C2.0515 0.984986 1.84383 1.10027 1.66885 1.24587C1.49387 1.39147 1.35502 1.56452 1.26028 1.75508C1.16554 1.94565 1.11677 2.14998 1.11677 2.35634C1.11677 2.5627 1.16554 2.76703 1.26028 2.95759C1.35502 3.14816 1.49387 3.32121 1.66885 3.46681C1.84383 3.61241 2.0515 3.72769 2.27993 3.80603C2.50837 3.88438 2.75306 3.92424 2.99998 3.92334L3.18772 3.92334C3.6849 3.92328 4.1617 3.75819 4.51326 3.46437C4.86482 3.17056 5.06236 2.77208 5.06244 2.35656V1.7294L5.81259 1.72963L5.81259 1.72918ZM6.00006 4.55005L6.00006 3.92334C6.24698 3.92424 6.49168 3.88438 6.72011 3.80603C6.94854 3.72769 7.15621 3.61241 7.33119 3.46681C7.50617 3.32121 7.64502 3.14816 7.73976 2.95759C7.8345 2.76703 7.88327 2.5627 7.88327 2.35634C7.88327 2.14998 7.8345 1.94565 7.73976 1.75508C7.64502 1.56452 7.50617 1.39147 7.33119 1.24587C7.15621 1.10027 6.94854 0.984986 6.72011 0.906641C6.49168 0.828297 6.24698 0.788433 6.00006 0.789336L5.81232 0.789336C5.31514 0.789399 4.83834 0.95449 4.48678 1.2483C4.13522 1.54212 3.93768 1.9406 3.93761 2.35612L3.93761 2.98327L3.18745 2.98305L3.18745 2.35634C3.18744 2.06822 3.25532 1.78292 3.38724 1.51674C3.51916 1.25055 3.71253 1.00869 3.9563 0.804959C4.20007 0.60123 4.48947 0.439627 4.80797 0.329376C5.12648 0.219126 5.46785 0.162388 5.81259 0.162403L6.00006 0.162624C6.69629 0.162624 7.364 0.39377 7.85631 0.805213C8.34862 1.21666 8.62519 1.77469 8.62519 2.35656C8.62519 2.93843 8.34862 3.49646 7.85631 3.90791C7.364 4.31935 6.69629 4.55049 6.00006 4.55049V4.55005Z"
                                        fill="#244D6F"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!--Card Hover-->
                    <div class="tooltip-data-filter" :style="{top: tooltipY[0], '--tooltipArrow': tooltipY[1]}" tabindex="0">
                        <ul class="t-texts">
                            <li>
                                <h3 v-if="entity['altLabel']" title="$t('EntitySelectView.also_known')">{{ $t("EntitySelectView.also_known") }}</h3>
                                <p v-if="entity['altLabel']">{{ entity["altLabel"] }}</p>
                            </li>
                            <li v-if="showTooltipDataset">
                                <h3 :title="$t('EntitySelectView.datasets')">{{ $t("EntitySelectView.datasets") }}</h3>
                                <p v-if="entity.datasets?.length == 0">Loading...</p>
                                <div v-for="(dataset, datasetKey) in entity.datasets" :key="datasetKey">
                                    <p v-if="datasetsToDisplay.includes(dataset.uri)">
                                        {{ dataset.name ?? dataset.uri }}
                                    </p>
                                    <p v-if="datasetsToHide.includes(dataset.uri)" class="hidden-dataset-tooltip">
                                        {{ dataset.name ?? dataset.uri }}
                                        <img src="@/assets/eye-off.svg" alt="Filtered Dataset"/>
                                    </p>
                                </div>
                            </li>
                            <li v-for="(value, pred) in entity"
                                :key="pred"
                            >
                                <div v-if="(pred as string) == 'dates'">
                                    <h3 :title="$t('EntitySelectView.birth')">{{ $t("EntitySelectView.birth") }}</h3>
                                    <p style="margin-bottom: 10px;">
                                        {{ datasetsToDisplay.includes((value as any)["birthDate"]?.graph) ? (value as any)["birthDate"].value : "Unknown" }},
                                        {{ datasetsToDisplay.includes((value as any)["birthPlace"]?.graph) ? (value as any)["birthPlace"].value : "Unknown" }}
                                    </p>
                                    <h3 :title="$t('EntitySelectView.death')">{{ $t("EntitySelectView.death") }}</h3>
                                    <p>
                                        {{ datasetsToDisplay.includes((value as any)["deathDate"]?.graph) ? (value as any)["deathDate"].value : "Unknown" }},
                                        {{ datasetsToDisplay.includes((value as any)["deathPlace"]?.graph) ? (value as any)["deathPlace"].value : "Unknown" }}
                                    </p>
                                </div>
                                <div v-if="((pred as string) == 'pursuits' || (pred as string) == 'works' || (pred as string) == 'creators' || (pred as string) == 'types' || (pred as string) == 'createDates' || (pred as string) == 'bornHere' || (pred as string) == 'diedHere' || (pred as string) == 'members' || (pred as string) == 'groups') && tooltipArrayIncludesDisplayDataset(value)">
                                    <h3 :title="$t('EntitySelectView.' + pred)">{{ $t("EntitySelectView." + pred) }}</h3>
                                    <p>{{ processTooltipArray(value) }}</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { Vue, Options } from "vue-class-component";
import SidePanelHeader from "@/components/SidePanelHeader.vue";
import NoResults from "@/components/NoResults.vue";
import { fetchWithRetry } from "../helper/API";

interface DisplayEntity {
    uri: string;
    label: string;
    type: string;
    links: string;
    datasets?: Array<any>;
    altLabel?: string;
}

@Options({
    components: {
        SidePanelHeader,
        NoResults
    },
    watch: {
        $route() {
            this.checkParamUpdate();
        },
    },
})
export default class EntitySelectView extends Vue {
    // This is the url for getting a list of candidate matches on a text
    reconciliationURL =
        "https://authority.lincsproject.ca/reconcile/any?queries=";

    // This is the url for filling out the tooltip cards
    apiURL = 
        "https://lincs-api.lincsproject.ca/api/";

    // This is the authToken needed for the lincs-api's rate limiting
    authToken = 
        "Bearer eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJVcG5aOHZaTGNUemhTMWtzdGN2NDVkR205QXYwUmlTbElaQ0YzZEhxR1JRIn0.eyJleHAiOjE3NDIzMjMyMjcsImlhdCI6MTcxMDc4NzIyNywianRpIjoiMjU2OTU1YjUtOTkzNi00OGZmLThjMmMtYjdkOWRiMTliZjc0IiwiaXNzIjoiaHR0cHM6Ly9rZXljbG9hay5saW5jc3Byb2plY3QuY2EvcmVhbG1zL2xpbmNzIiwiYXVkIjpbInZlcnNkLXVpIiwicmVzZWFyY2hzcGFjZSIsImFkbWluLWNsaSIsImJyb2tlciIsImFjY291bnQiXSwic3ViIjoiYmQ5MTRkMTEtZmVkMi00ODllLWI0MGQtNDk0YjkzZDFmOGU4IiwidHlwIjoiQmVhcmVyIiwiYXpwIjoibGluY3MtYXBpIiwiYWNyIjoiMSIsImFsbG93ZWQtb3JpZ2lucyI6WyIvKiJdLCJyZWFsbV9hY2Nlc3MiOnsicm9sZXMiOlsiVXNlciIsImRlZmF1bHQtcm9sZXMtbGluY3MiLCJvZmZsaW5lX2FjY2VzcyIsInVtYV9hdXRob3JpemF0aW9uIl19LCJyZXNvdXJjZV9hY2Nlc3MiOnsidmVyc2QtdWkiOnsicm9sZXMiOlsidXNlciJdfSwicmVzZWFyY2hzcGFjZSI6eyJyb2xlcyI6WyJjb250cmlidXRvciIsImd1ZXN0Il19LCJhZG1pbi1jbGkiOnsicm9sZXMiOlsiYWRtaW4iXX0sImJyb2tlciI6eyJyb2xlcyI6WyJyZWFkLXRva2VuIl19LCJhY2NvdW50Ijp7InJvbGVzIjpbIm1hbmFnZS1hY2NvdW50IiwibWFuYWdlLWFjY291bnQtbGlua3MiLCJ2aWV3LXByb2ZpbGUiXX19LCJzY29wZSI6ImVtYWlsIHByb2ZpbGUgYXZhdGFyX3VybCIsImVtYWlsX3ZlcmlmaWVkIjpmYWxzZSwiY2xpZW50SG9zdCI6IjcwLjc1LjExMi4xMSIsInByZWZlcnJlZF91c2VybmFtZSI6InNlcnZpY2UtYWNjb3VudC1saW5jcy1hcGkiLCJjbGllbnRBZGRyZXNzIjoiNzAuNzUuMTEyLjExIiwiY2xpZW50X2lkIjoibGluY3MtYXBpIn0.ivjIKG0hWe5ULAseATO4RYUmR8GCEZaTnbE70zoT-xdNxz-jXZcGmTLnGf_t34X3BEX13u-unCPGv7T5M5Wnn6f_ohG5WW8H18oSxiAflSLue3TEpgEODACXS2rzP8gDODof2cC1MSDL9E_YI4lpQ6XAl8aMb-ojhzjZ4RJUbjXq3epQ-rmvg2R5DYtKSPtn8f1o2zooGVZg8lO_Ymi9tAxqr7PsayMXExsD2R_SShrUD9DS-1Vfvrh8a1dxdquUtZ1GHc90Ms08BxdV8hBe0Q8UvQorZgQ6dOUnKUId6250qcENqXjvbttN--FPt4z8BZDphaA38Itm-q0UV4YesQ";

    // This is the list of allowed candidate types
    highlightTypeFilter = [
        "Actor",
        "Person",
        "Group",
        "Work",
        "Expression",
        "Human-Made Object",
        "Place",
    ];

    showResults = false;
    highlights: Array<string> = [];
    entities: Array<DisplayEntity> = [];
    displayedEntities: Array<DisplayEntity> = [];
    loading = false;
    focusedHighlight = 0;
    allTypes: Array<string> = [];
    typeFilter = "all";
    pluginSettings: any = {};
    filterSettings: any = {};
    modifiedSettingsCount = 0;
    mouseY = 0;
    showTooltipDataset = true;
    requestId = "";
    datasetsToDisplay: any = [];
    datasetsToHide: any = [];
    entitySearchValue = "";
    entitySearchNoResults = false;
    activeTooltipContainer: HTMLElement | null = null;
    activeCardTop = 0;
    activeEntityIndex = -1;

    get tooltipY() {
        // Force recalculation by using activeEntityIndex as dependency
        const entityIndex = this.activeEntityIndex;
        
        let element = null;
        if (this.activeTooltipContainer && entityIndex >= 0) {
            element = this.activeTooltipContainer.querySelector('.tooltip-data-filter');
        }

        // Use the stored card position from hover time
        const cardTop = this.activeCardTop;

        // Snap tooltip to the top of the card
        let top = cardTop - 100;
        let arrow = 20;

        // Check if tooltip would overflow the viewport
        if (element && cardTop + (element as any).offsetHeight + 15 > self.innerHeight) {
            // Position tooltip so it fits within viewport
            top = self.innerHeight - (element as any).offsetHeight - 112;

            // Adjust arrow to point to the card
            arrow = this.mouseY - top - 110;
            arrow = arrow < 20 ? 20 : arrow;
            arrow = arrow > (element as any).offsetHeight - 40 ? (element as any).offsetHeight - 40 : arrow;
        }
        return [
            top.toString() + "px",
            arrow.toString() + "px",
        ];
    }

    mounted() {
        this.$i18n.locale = this.$route.params.locale as string;
        document.body.addEventListener("mousemove", this.setMouseY);

        this.checkParamUpdate();
        window.addEventListener("message", (event) => {
            if (event.data.type == "done-highlights") {
                this.entitySearchNoResults = false;
                this.highlights = event.data.highlights;
            }

            if (event.data.type == "return-filters" && event.data.id == this.requestId) {
                const filters = event.data.filters.activeFilters;

                this.datasetsToDisplay = filters.graph.filters
                    .filter((filter: any) => filter.active)
                    .map((filter: any) => filter.value);
                this.datasetsToHide = filters.graph.filters
                    .filter((filter: any) => !filter.active)
                    .map((filter: any) => filter.value);

                filters.type.filters = filters.type.filters
                    .filter((filter: any) => filter.active)
                    .map((filter: any) => filter.label);
                filters.graph.filters = filters.graph.filters
                    .filter((filter: any) => filter.active)
                    .map((filter: any) => filter.label);
                this.filterSettings = filters;

                if (this.$route.params.text != "") {
                    this.getEntities(
                        this.$route.params.text as string,
                        JSON.parse(
                            JSON.stringify(event.data.filters.activeFilters),
                        ),
                    );
                }
            }

            if (event.data.type == "return-settings" && event.data.id == this.requestId) {
                this.pluginSettings = event.data.settings;
                this.pluginSettings.descriptor = [];
                this.countModifiedSettings();
            }

            if (event.data.type == "return-exact-match") {
                this.loading = false;
                this.entitySearchNoResults = true;
            }
        });
    }

    unmounted() {
        document.body.removeEventListener("mousemove", this.setMouseY);
    }

    setMouseY(event: any) {
        this.mouseY = event.clientY;
    }

    onBack() {
        this.entitySearchNoResults = false;
        window.parent.postMessage({ type: "exit-graph-display" }, "*");
    }

    checkParamUpdate() {
        this.requestId = this.createRequestId(10);
        window.parent.postMessage({ type: "contract-sidebar-width" }, "*");
        window.parent.postMessage({ type: "get-filters", id: this.requestId }, "*");
        window.parent.postMessage({ type: "get-settings", id: this.requestId }, "*");
        if (this.$route.params.text != "") {
            this.showResults = true;
            this.loading = true;
        } else {
            this.entities = [];
            this.displayedEntities = [];
            this.allTypes = [];
            this.showResults = false;
            this.typeFilter = "all";
        }
    }

    // To fetch all the available entities on hover
    getEntities(text: string, filters: any) {
        filters.type.filters = filters.type.filters
            .filter((filter: any) => filter.active)
            .map((filter: any) => filter.value);
        filters.graph.filters = filters.graph.filters
            .filter((filter: any) => filter.active)
            .map((filter: any) => filter.value);

        const properties = [];
        for (const graph of filters.graph.filters) {
            properties.push({
                pid: "http://graph.lincsproject.ca/",
                v: graph,
            });
        }

        const query = {
            q1: {
                query: text,
                types: filters.type.filters,
                properties: properties,
            },
        };

        fetchWithRetry(
            this.reconciliationURL + encodeURIComponent(JSON.stringify(query)),
            {
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            },
        )
            .then((response) => {
                if (response.ok) {
                    return response.json();
                } else {
                    console.log(["QUERY-ERROR", response]);
                }
            })
            .then((response) => {
                this.loading = false;
                this.entities = [];
                this.displayedEntities = [];
                for (const result of response.q1.result) {
                    result.type =
                        result?.type[0].name != ""
                            ? result.type[0].name
                            : result.type[0].id.split("/").pop();
                    if (
                        this.entities.length < 15 &&
                        this.highlightTypeFilter.includes(result.type)
                    ) {
                        const datasetObj = {
                            uri: result.graph,
                        };
                        this.getDatasetLabel(datasetObj);
                        
                        this.entities.push({
                            uri: result.id,
                            label: result.name,
                            type: result.type,
                            links: result.links,
                            datasets: [datasetObj],
                        });
                        if (!this.allTypes.includes(result.type)) {
                            this.allTypes.push(result.type);
                        }
                    }
                }
                window.parent.postMessage({ type: "expand-sidebar-width" }, "*");
                this.displayedEntities = this.entities;
            });
    }

    // Opens up bubble/sidepanel to display the categories based on entity selection
    selectEntity(label: string, uri: string) {
        window.parent.postMessage({ type: "contract-sidebar-width" }, "*");
        window.parent.postMessage(
            { type: "show-entity-graph", label: label, uri: uri },
            "*",
        );
    }

    // Closes the sidebar and all the process happening with it
    exitExtension() {
        window.parent.postMessage({ type: "exit" }, "*");
    }

    // Stops the scanning of the page
    stopScan() {
        window.parent.postMessage({ type: "stop-scan" }, "*");
    }

    // Go to specific highlight
    focusOnHighlight(index: number) {
        this.focusedHighlight = index + 1;
        window.parent.postMessage(
            {
                type: "scroll-to-highlight",
                highlightNum: this.focusedHighlight - 1,
                selectHighlightImmediatly: true,
            },
            "*",
        );
    }

    // Go to the next highlight
    focusOnNextHighlight() {
        if (this.focusedHighlight != Number(this.$route.params.numHighlights)) {
            this.focusedHighlight += 1;
            window.parent.postMessage(
                {
                    type: "scroll-to-highlight",
                    highlightNum: this.focusedHighlight - 1,
                    selectHighlightImmediatly: false,
                },
                "*",
            );

            const element = document.getElementById(
                "highlight-" + (this.focusedHighlight - 1).toString(),
            );
            if (element != null) {
                element.scrollIntoView({
                    behavior: "auto",
                    block: "center",
                    inline: "center",
                });
            }
        }
    }

    // Go to the previous highlight
    focusOnPreviousHighlight() {
        if (this.focusedHighlight != 1) {
            this.focusedHighlight -= 1;
            window.parent.postMessage(
                {
                    type: "scroll-to-highlight",
                    highlightNum: this.focusedHighlight - 1,
                    selectHighlightImmediatly: false,
                },
                "*",
            );

            const element = document.getElementById(
                "highlight-" + (this.focusedHighlight - 1).toString(),
            );
            if (element != null) {
                element.scrollIntoView({
                    behavior: "auto",
                    block: "center",
                    inline: "center",
                });
            }
        }
    }

    // Filter the entities based on their type [Eg: People, Place, Work, etc.]
    filterByType() {
        if (this.typeFilter == "all") {
            this.displayedEntities = this.entities;
        } else {
            this.displayedEntities = this.entities.filter((value) => {
                if (value.type == this.typeFilter) {
                    return true;
                }
                return false;
            });
        }
    }

    // Helper function to check modified settings from the popup
    countModifiedSettings() {
        const { allSettings, activeSettings, descriptor } = this.pluginSettings;

        const isHighlightSwitchChanged =
            allSettings.highlight.settings.displayFirstHighlight.value !==
            activeSettings.highlight.settings.displayFirstHighlight.value;
        const isScoreChanged =
            allSettings.highlight.settings.highlightRelevanceScore.value !==
            activeSettings.highlight.settings.highlightRelevanceScore.value;

        this.modifiedSettingsCount =
            (isHighlightSwitchChanged ? 1 : 0) + (isScoreChanged ? 1 : 0);

        if (isHighlightSwitchChanged) {
            descriptor.push("Display first occurrence only");
        }

        if (isScoreChanged) {
            const currentScore =
                activeSettings.highlight.settings.highlightRelevanceScore.value;
            descriptor.push(`Relevance score: ${currentScore}`);
        }
    }

    // Get entity information for the tooltop dropdown
    tooltipActive(entity: any, event: MouseEvent, index: number) {
        // Use currentTarget directly since mouseenter is on the container itself
        this.activeTooltipContainer = event.currentTarget as HTMLElement;
        this.activeEntityIndex = index;
        
        // Capture the card's position at hover time
        const rect = this.activeTooltipContainer.getBoundingClientRect();
        this.activeCardTop = rect.top;
        
        if (!entity['loaded']) {
            entity['loaded'] = true;

            fetchWithRetry(this.apiURL + "entity", {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': this.authToken,
                },
                method: "POST",
                body: JSON.stringify({ uris: [entity['uri']], language: this.$i18n.locale })
            }).then((response) => {
                if (response.status == 200) {
                    return response.json();
                }
                throw new Error("FAILURE");
            }).then((response) => {
                if('outgoing' in response[0]) {
                    for (const triple of response[0]['outgoing']) {
                        if (triple['predicate'] == "http://www.w3.org/2004/02/skos/core#altLabel") {
                            entity['altLabel'] = entity['altLabel'] == null ? 
                                                    triple['object'] : 
                                                    entity['altLabel'] + ", " + triple['object'];
                        }
                    }
                }
                if (entity['altLabel']) {
                    entity['altLabel'] = entity['altLabel'].length > 75 ? entity['altLabel'].substring(0, 75) + "..." : entity['altLabel'];
                }

                this.getCategoryTooltipInfo(entity);
            }).catch((error) => { console.log(error); });
        }
    }

    getDatasetLabel(datasetObj: any) {
        fetchWithRetry(this.apiURL + "dataset", {
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                'Authorization': this.authToken,
            },
            method: "POST",
            body: JSON.stringify({
                uri: datasetObj.uri,
                language: this.$i18n.locale
            }),
        })
        .then((response) => {
            if (response.ok) {
            return response.json();
            }
            throw new Error("FAILURE");
        })
        .then(async (response) => {
            for (const pred of response) {
                if ((pred.predicate == "http://www.cidoc-crm.org/cidoc-crm/P1_is_identified_by") && (pred.type == "http://vocab.getty.edu/aat/300417227" )) {
                    datasetObj.name = pred.object;

                    // This is used to refresh the display to show these labels
                    // Fixes a reactivity bug
                    this.showTooltipDataset = false;
                    await this.$nextTick();
                    this.showTooltipDataset = true;

                    break;
                }
            }
        })
        .catch(() => {
            console.log("Query Error");
        });
    }

    // Get category specific entity information for the tooltop dropdown
    getCategoryTooltipInfo(entity: any) {
        const endpoint = this.getEndpoint(entity.type);
        if (endpoint) {
            fetchWithRetry(this.apiURL + endpoint, {
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': this.authToken,
                },
                method: "POST",
                body: JSON.stringify({ uris: [entity['uri']], language: this.$i18n.locale })
            }).then((response) => {
                if (response.status == 200) {
                    return response.json();
                }
                throw new Error("FAILURE");
            }).then((response) => {
                for (const predicate of response[0].statements) {
                    const dataset = predicate.graph;
                    if (dataset && !entity['datasets'].map((dataset: any) => dataset.uri).includes(dataset)) {
                        const datasetObj = {
                            uri: dataset,
                        };
                        entity['datasets'].push(datasetObj);
                        this.getDatasetLabel(datasetObj);
                    }

                    if (predicate.parsed) {
                        continue;
                    }

                    switch (predicate.type) {
                        case "birthDate":
                        case "birthPlace":
                        case "deathDate":
                        case "deathPlace":
                            if (!entity['dates']) {
                                entity['dates'] = {};
                            }
                            entity['dates'][predicate.type] = {
                                value: predicate.value,
                                graph: predicate.graph
                            };
                            break;
                        case "pursuit":
                        case "work":
                        case "creator":
                        case "type":
                        case "bornHere":
                        case "diedHere":
                        case "member":
                        case "group": 
                        case "createDate": {
                            const displayProp = (predicate.type == "bornHere" || predicate.type == "diedHere") ? predicate.type : (predicate.type + "s");
                            entity[displayProp] = [];
                            for (const predicateCheck of response[0].statements) {
                                if (predicateCheck.type == predicate.type) {
                                    predicateCheck.parsed = true;
                                    entity[displayProp].push({
                                        value: (predicateCheck.value ?? predicateCheck.uri), 
                                        graph: predicateCheck.graph 
                                    });
                                }
                            }
                            break;
                        }
                    }
                }
            }).catch((error) => { console.log(error); });
        }
    }

    getEndpoint(type: string) {
        switch(type) {
            case "Person":
                return "person/statements";
            case "Work":
                return "work/statements";
            case "Group":
                return "group/statements";
            case "Place":
                return "place/statements";
            default:
                return null;
        }
    }

    createRequestId(length: number) {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        const charactersLength = characters.length;
        let counter = 0;
        while (counter < length) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
            counter += 1;
        }
        return result;
    }

    processTooltipArray(arr: any) {
        let str = "";
        for (const pred of arr) {
            if (this.datasetsToDisplay.includes(pred.graph)) {
                str += ", " + pred.value;
            }
        }
        str = str.slice(2);
        str = str.length > 75 ? str.substring(0, 75) + "..." : str;
        return str;
    }

    tooltipArrayIncludesDisplayDataset(arr: any) {
        const map = arr.map((pred: any) => pred.graph);
        const filter = map.filter((graph: any) => this.datasetsToDisplay.includes(graph));
        return filter.length > 0;
    }

    // Perform an exact match scan
    exactMatchScan() {
        this.loading = true;
        window.parent.postMessage(
            {
                type: "exact-match-scan",
                text: this.entitySearchValue
            },
            "*",
        );
    }
}
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap");

.header-btn-block {
    position: relative;
    margin: 0;
    display: flex;
    align-items: center;
    padding: 10px 15px 10px 15px;
}

.header-btn-block:hover {
    background-color: #edf6f8;
    transition: 0.2s ease all;
}

.back-btn {
    font-size: 16px;
    text-decoration: none;
    display: flex;
    align-items: center;
    color: #333333;
    cursor: pointer;
}

.back-btn img {
    margin-right: 10px;
}

.back-btn:hover {
    text-decoration: none;
}

.loader-block-select {
    position: fixed;
    top: calc(50vh - 35px);
    left: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    z-index: 999;
}

#loading-bar-spinner.spinner {
    animation: loading-bar-spinner 1000ms linear infinite;
}

#loading-bar-spinner.spinner .spinner-icon {
    width: 70px;
    height: 70px;
    border: solid 10px transparent;
    border-top-color: #083943 !important;
    border-left-color: #083943 !important;
    border-radius: 50%;
}

@keyframes loading-bar-spinner {
    0% {
        transform: rotate(0deg);
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
        transform: rotate(360deg);
    }
}

.progress-bar-background {
    width: 300px;
    background-color: #dbdfe0;
    border-radius: 20px;
    overflow: hidden;
}

.progress-bar-foreground {
    width: 10%;
    height: 30px;
    border-radius: 20px;
    background-color: #083943;
    text-align: center;
    line-height: 30px;
    color: white;
}

.title {
    text-decoration:underline;
    text-decoration-style: dotted;
    text-align: left;
    font-size: 16px;
    color: #083943;
    margin-bottom: 6px;
    height: 100%;
}

.display-row {
    display: flex;
    justify-content: space-between;
    padding: 0px 20px;
    align-items: center;
    height: 52px;
}

.display-row-offcenter {
    display: flex;
    padding-left: 12px;
}

.num-results-info {
    margin-top: 10px;
    margin-bottom: 0px;
}

.arrow {
    border: solid #2c3e50;
    border-width: 0 3px 3px 0;
    display: inline-block;
    padding: 3.5px;
    margin-right: 5px;
}

.left {
    transform: rotate(135deg);
    -webkit-transform: rotate(135deg);
}

.cancel-button {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    margin-left: 10px;
    padding: 5px 8px;
    background-color: #f8ecec;
    border: 1px solid #b54040;
    color: #b54040;
    border-radius: 20px;
    cursor: pointer;
}

.cancel-button:hover {
    background-color: #EFD2D2;
}

.num-highlight-text {
    margin-right: 10px;
}

.select-matching-entities {
    text-align: left;
    margin: 4px 0px 10px 10px;
    color: #083943;
    font-weight: 600;
    font-size: 16px;
}

.match-entities {
    text-align: left;
    font-size: 14px;
    margin: 4px 0px 10px 10px;
    font-family: "Inter", sans-serif;
    color: #525455;
}

.match-title {
    text-align: left;
    font-size: 20px;
    margin: 5px 0px 20px 10px;
    font-family: "Inter", sans-serif;
    color: #333333;
}

.matching-entities {
    position: relative;
    padding: 0px 12px;
}

.type-filter {
    font-family: 'Inter', sans-serif;
    color:#5E6364;
    width: calc(100% - 10px);
    height: 38px;
    border-radius: 10px;
    font-size: 14px;
    padding: 10px;
    margin-bottom: 10px;
    border-color: #cccccc;
}

.entity-wrapper {
    height: calc(100vh - 260px);
    overflow-y: auto;
    overflow-x: hidden;
    font-family: "Inter", sans-serif;
}

.entity-wrapper::after {
  content: "";
  display: block;
  height: 250px;
}

.entity {
    border: 1px solid #cccccc;
    border-radius: 12px;
    margin: 0px 0px 10px 0px;
    cursor: pointer;
    overflow-wrap: break-word;
}

.entity:hover {
    background-color: #EDF6F8;
}

.entity-label {
    text-align: left;
    margin: 0px;
    padding: 8px 0px 5px 12px;
    font-size: 16px;
    text-overflow: ellipsis;
    width: 300px;
    overflow: hidden;
    white-space: nowrap;
    font-family: "Inter", sans-serif;
    color: #333333;
    font-weight: 600;
}

.entity-uri {
    text-align: left;
    margin: 0px;
    padding: 0px 0px 12px 12px;
    font-size: 14px;
    text-overflow: ellipsis;
    width: 300px;
    overflow: hidden;
    white-space: nowrap;
    font-family: "Inter", sans-serif;
    color: #414343;
    font-weight: 400;
}

.entity-info-block {
    display: flex;
    flex-direction: row;
    padding: 0px 0px 8px 12px;
    align-items: center;
}

.entity-type {
    font-size: 14px;
    padding-right: 5px;
    font-family: "Inter", sans-serif;
    color: #414343;
}

.entity-links-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    border: 1px solid #244d6f;
    padding: 2px;
    border-radius: 30px;
    margin-left: 5px;
}

.entity-links {
    display: block;
    font-size: 12px;
    color: #244d6f;
    margin-right: 2px;
    font-family: "Inter", sans-serif;
}

.help-text {
    margin: 10px;
    margin-bottom: 5px;
    text-align: right;
}

.doc-text {
    margin: 10px;
    margin-top: 5px;
    margin-bottom: 5px;
    text-align: right;
}

.doc-link {
    text-decoration: none;
    color: #083943;
}

.truncate-text {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

/* Tooltip Start */

.tooltip-main-data {
    width: auto;
    display: inline-block;
}

.tooltip-data-filter {
    opacity: 0;
    visibility: hidden;
    position: absolute;
    width: 280px;
    background-color: #081920;
    padding: 5px 5px;
    z-index: 999;
    bottom: -80px;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    transition-delay: 100ms;
}

.tooltip-main-data.dataset-data .tooltip-data-filter {
    width: 162.5px;
}

.tooltip-main-data.setting-data .tooltip-data-filter {
    width: 155px;
}

.tooltip-main-data.entity-data .tooltip-data-filter {
    width: 175px;
    margin-bottom: 10px;
    left: -190px;
    top: 70px;
    bottom: unset;
}

.tooltip-main-data.entity-data .tooltip-data-filter .t-texts {
    text-align: left;
    padding-top: 4px;
}

.tooltip-main-data.entity-data .tooltip-data-filter .t-texts h3 {
    color: #BAD8DE;
    margin: 0px;
}

.tooltip-main-data.entity-data .tooltip-data-filter .t-texts li {
    margin-top: 2px;
    margin-bottom: 10px;
}

.tooltip-main-data.entity-data .tooltip-data-filter .t-texts p {
    margin-top: 2px;
    margin-bottom: 0px;
}

.tooltip-main-data.entity-data .tooltip-data-filter:before {
    top: var(--tooltipArrow);
    left: 180px;
    transform: rotate(90deg);
}

.tooltip-data-filter:before {
    content: "";
    position: absolute;
    top: -10px;
    left: 10px;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #081920;
}

.tooltip-data-filter .t-texts {
    margin: 0;
    font-size: 12px;
    color: #ffffff;
    height: 60px;
    overflow: auto;
}

.tooltip-main-data.entity-data .tooltip-data-filter .t-texts {
    height: 100%;
    overflow: hidden;
    word-wrap: break-word;
}

.tooltip-data-filter .t-texts span {
    display: inline-block;
    text-align: left;
    margin-bottom: 10px;
}

.tooltip-data-filter .t-texts span:last-child {
    margin-bottom: 0px;
}

.settings-block {
    position: relative;
    width: auto;
    height: auto;
    padding: 16px 12px;
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid #ddd;
}

.active-setting {
    position: relative;
    display: flex;
    align-items: center;
    width: auto;
    margin-bottom: 10px;
}

.active-setting:last-child {
    margin-bottom: 0px;
}

.setting-cog {
    display: flex;
}

.active-setting-text {
    color: #107386;
    font-weight: 500;
    font-size: 14px;
    margin: 0 0 0 5px;
    padding: 3px 8px;
    background-color: #def3f7;
}

.active-setting-text:focus + .tooltip-data-filter {
    opacity: 1;
    visibility: visible;
}

.tooltip-main-data:hover .tooltip-data-filter {
    opacity: 1;
    visibility: visible;
}

.tooltip-main-data:focus-within .tooltip-data-filter {
    opacity: 1;
    visibility: visible;
}

.hidden-dataset-tooltip {
    display: flex !important;
}

.hidden-dataset-tooltip img {
    margin-left: 5px;
    width: 12px;
}

/* Tooltip End */

/* Matched Highlights Start */

.matched-entities {
    display: block;
    position: relative;
    text-align: left;
    border: 1px solid #ddd;
    margin: 0 0 10px 0;
    border-radius: 12px;
    padding: 12px 12px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    color: #333;
}

.matched-entities.hovered {
    background-color: rgb(194, 229, 255);
}

.matched-entities.hovered:hover {
    background-color: rgb(194, 229, 255);
}

.matched-entities:hover {
    background-color: #edf6f8;
}

.matched-entities:focus {
    background-color: transparent;
    border-color: #107386;
}

.matched-entities h5 {
    margin: 0;
    font-size: 14px;
    color: #2c3e50;
}

.matched-entity-section {
    height: calc(100vh - 280px);
    overflow: auto;
    position: relative;
    padding: 5px 15px 10px 15px;
}

/* Matched Highlights End */

/* Search Bar Start */

.entity-search-bar-container {
    margin: 20px 0px 20px 0px;
}

.entity-search-bar {
    width: 300px;
    height: 30px;
    border-radius: 10px;
    border: 1px solid #083943;
    font-weight: 600;
    font-family: Inter, sans-serif;
    font-size: 14px;
}

.entity-search-bar-failure, .entity-search-bar-failure:focus, .entity-search-bar-failure:active {
    border-color: red;
    outline-color: red;
}

.entity-failure-message {
    color: red;
    margin: 0px;
    margin-top: 5px;
    font-size: 12px;
}

/* Search Bar End */
</style>
